import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, ArrowLeft } from 'lucide-react';
import { FaArrowLeft } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Ebooks = ({ user }) => {
  const navigate = useNavigate();
  const [ebooks, setEbooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userPlan, setUserPlan] = useState(user?.subscription_plan?.toLowerCase()?.trim() || 'none');
  const [profileError, setProfileError] = useState(false);
  const [hasEbookAccess, setHasEbookAccess] = useState(false);
  const [sortBy, setSortBy] = useState('title-asc'); // Ordenamiento

  // Verificar si el usuario está logueado
  const isLoggedIn = !!user || localStorage.getItem('deisycfit_logged_in') === 'true';
  
  // Usar el plan del user prop directamente
  const effectiveUserPlan = user?.subscription_plan?.toLowerCase()?.trim() || 'none';
  const isFreePlan = !effectiveUserPlan || effectiveUserPlan === 'none' || effectiveUserPlan === 'free' || effectiveUserPlan === '';
  // Considerar como plan pago cualquier plan que no sea free/none/vacío
  const hasPaidPlan = effectiveUserPlan && effectiveUserPlan !== 'none' && effectiveUserPlan !== 'free' && effectiveUserPlan !== '';

  useEffect(() => {
    fetchEbooks();
    fetchUserInfo();
  }, []);

  const fetchUserInfo = async () => {
    try {
      setProfileError(false);
      const token = localStorage.getItem('deisycfit_token');
      const response = await axios.get(`${API}/user/profile`, {
        withCredentials: false,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });
      setUserPlan(response.data.subscription_plan || 'none');
      setHasEbookAccess(response.data.has_ebook_access || hasPaidPlan);
    } catch (error) {
      console.error('Error fetching user info:', error);
      // No degradar a 'none' por un error de request: se conserva el plan del prop y se avisa.
      setProfileError(true);
      setHasEbookAccess(hasPaidPlan);
    }
  };

  const fetchEbooks = async () => {
    try {
      const response = await axios.get(`${API}/ebooks`, { withCredentials: false });
      
      if (response.data.length === 0) {
        // Ebooks de muestra con campo is_free
        setEbooks([
          {
            id: '1',
            title: 'Recetario Fitness - 50 Recetas',
            description: '50 recetas deliciosas y saludables para tu día a día. Incluye desayunos, almuerzos, cenas y snacks.',
            price: 25,
            category: 'Recetas',
            is_free: false,
            thumbnail_url: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=500&fit=crop'
          },
          {
            id: '2',
            title: 'Guía de Suplementación',
            description: 'Todo lo que necesitas saber sobre suplementos deportivos para maximizar tus resultados.',
            price: 20,
            category: 'Nutrición',
            is_free: false,
            thumbnail_url: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=500&fit=crop'
          },
          {
            id: '3',
            title: 'Plan de 12 Semanas',
            description: 'Programa completo de transformación corporal de 12 semanas con rutinas y nutrición.',
            price: 35,
            category: 'Entrenamiento',
            is_free: false,
            thumbnail_url: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=500&fit=crop'
          },
          {
            id: '4',
            title: 'Postres Saludables',
            description: 'Disfruta sin culpa con estos postres fit bajos en calorías y altos en proteína.',
            price: 18,
            category: 'Recetas',
            is_free: false,
            thumbnail_url: 'https://images.unsplash.com/photo-1565958011703-44f9829ba187?w=400&h=500&fit=crop'
          }
        ]);
      } else {
        setEbooks(response.data);
      }
    } catch (error) {
      console.error('Error fetching ebooks:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar ebooks accesibles para el usuario
  const getAccessibleEbooks = () => {
    if (hasPaidPlan) {
      return ebooks; // Usuarios con plan ven todos
    }
    // Usuarios FREE solo ven ebooks gratuitos
    return ebooks.filter(ebook => ebook.is_free === true);
  };

  // Verificar si un ebook específico es accesible
  const canAccessEbook = (ebook) => {
    return hasPaidPlan || ebook.is_free === true;
  };

  // Contar ebooks gratuitos
  const freeEbookCount = ebooks.filter(e => e.is_free === true).length;

  // Ordenar ebooks
  const sortEbooks = (ebookList) => {
    const [field, order] = sortBy.split('-');
    return [...ebookList].sort((a, b) => {
      let aVal, bVal;
      
      if (field === 'title') {
        aVal = a.title?.toLowerCase() || '';
        bVal = b.title?.toLowerCase() || '';
      } else if (field === 'date') {
        aVal = new Date(a.created_at || 0).getTime();
        bVal = new Date(b.created_at || 0).getTime();
      } else if (field === 'price') {
        aVal = a.price || 0;
        bVal = b.price || 0;
      } else if (field === 'category') {
        aVal = a.category?.toLowerCase() || '';
        bVal = b.category?.toLowerCase() || '';
      }
      
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const getEbookPrice = (price, originalPriceFromDB) => {
    // Si el ebook tiene un precio gancho configurado, usarlo como "original"
    const displayOriginal = originalPriceFromDB || price;
    const actualPrice = price;
    
    // Calcular descuento si hay precio gancho
    const hasStrikePrice = originalPriceFromDB && originalPriceFromDB > price;
    const strikeDiscount = hasStrikePrice 
      ? Math.round((1 - price / originalPriceFromDB) * 100) + '%'
      : null;
    
    if (userPlan === 'gold') {
      return { 
        original: displayOriginal, 
        strikePrice: hasStrikePrice ? originalPriceFromDB : null,
        discounted: Math.round(actualPrice * 0.5), 
        discount: '50%', 
        free: false,
        strikeDiscount
      };
    }
    if (userPlan === 'premium' || userPlan === 'competitive') {
      return { 
        original: displayOriginal, 
        strikePrice: hasStrikePrice ? originalPriceFromDB : null,
        discounted: Math.round(actualPrice * 0.7), 
        discount: '30%', 
        free: false,
        strikeDiscount
      };
    }
    if (userPlan === 'basic') {
      return { 
        original: displayOriginal, 
        strikePrice: hasStrikePrice ? originalPriceFromDB : null,
        discounted: Math.round(actualPrice * 0.8), 
        discount: '20%', 
        free: false,
        strikeDiscount
      };
    }
    return { 
      original: actualPrice, 
      strikePrice: hasStrikePrice ? originalPriceFromDB : null,
      discounted: actualPrice, 
      discount: hasStrikePrice ? strikeDiscount : '0%', 
      free: false,
      strikeDiscount
    };
  };

  const handlePurchaseOrDownload = (ebook) => {
    if (hasEbookAccess) {
      if (ebook.file_url) {
        window.open(ebook.file_url, '_blank');
        toast.success('Descargando ebook...');
      } else {
        toast.info('Este ebook estará disponible pronto');
      }
    } else {
      const prices = getEbookPrice(ebook.price, ebook.original_price);
      navigate(`/checkout?plan=ebook&id=${ebook.id}&price=${prices.discounted}&originalPrice=${prices.original}&title=${encodeURIComponent(ebook.title)}&discount=${prices.discount}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div 
          className="w-16 h-16 rounded-full animate-pulse"
          style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative" style={{ backgroundColor: '#0a0a0a' }}>
      {profileError && (
        <div className="relative z-10 max-w-3xl mx-auto mt-4 px-4 py-3 rounded-xl text-sm text-white flex items-center justify-between gap-3" style={{ background: 'rgba(255,80,80,0.15)', border: '1px solid rgba(255,80,80,0.4)' }} data-testid="ebooks-profile-error">
          <span>No pudimos verificar tu plan. Si no ves tus ebooks, vuelve a intentarlo o inicia sesión de nuevo.</span>
          <button onClick={fetchUserInfo} className="px-3 py-1 rounded-full bg-white/10 hover:bg-white/20 font-semibold" data-testid="ebooks-profile-retry">Reintentar</button>
        </div>
      )}
      {/* Mesh gradient background */}
      <div 
        className="fixed inset-0 z-0 pointer-events-none"
        style={{
          background: 'radial-gradient(circle at 80% 20%, rgba(0, 240, 255, 0.08), transparent 50%), radial-gradient(circle at 20% 80%, rgba(var(--brand-primary-glow), 0.12), transparent 40%)'
        }}
      />
      
      {/* Navbar */}
      <nav className="fixed w-full top-0 z-50 bg-[#0a0a0a]/95 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="text-2xl font-bold cursor-pointer" onClick={() => navigate('/')}>
            <span 
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              DeisyCFit
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-white"
            >
              Inicio
            </Button>
            <Button 
              onClick={() => navigate('/dashboard')}
              className="text-white rounded-full px-6 font-semibold"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
            >
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-28 pb-12 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Botón Volver */}
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="mb-8 rounded-full px-6 font-semibold"
            style={{ 
              background: 'transparent',
              border: '2px solid var(--brand-primary)',
              color: 'var(--brand-primary)'
            }}
          >
            <FaArrowLeft className="mr-2" /> Volver al Dashboard
          </Button>
          
          <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div 
              className="inline-flex items-center justify-center w-20 h-20 rounded-2xl text-white mb-6"
              style={{ background: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
            >
              <svg className="w-10 h-10" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
              </svg>
            </div>
            <p 
              className="text-xs font-bold uppercase tracking-[0.3em] mb-4"
              style={{ color: 'var(--brand-primary)' }}
            >
              Contenido exclusivo
            </p>
            <h1 
              className="text-4xl lg:text-6xl font-bold text-white mb-4 uppercase tracking-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Biblioteca de{' '}
              <span 
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: 'linear-gradient(135deg, var(--brand-primary), #00F0FF)' }}
              >
                Ebooks
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {hasEbookAccess 
                ? 'Descarga todos los ebooks incluidos en tu plan'
                : 'Contenido premium para alcanzar tus objetivos de salud y bienestar'}
            </p>
          </motion.div>
          </div>
        </div>
      </section>

      {/* Discount Info */}
      {!hasEbookAccess && (
        <section className="px-6 pb-12">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-[#1a1a1a] rounded-2xl p-6 border border-white/10"
            >
              <h3 className="font-bold text-lg text-white mb-4 flex items-center gap-2">
                <svg className="w-5 h-5 text-[var(--brand-primary)]" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z" />
                </svg>
                Descuentos por Plan
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-[#222] rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Sin Plan</p>
                  <p className="text-white font-bold">Precio Normal</p>
                </div>
                <div className="bg-[#222] rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Fit Evolution</p>
                  <p className="text-[var(--brand-primary)] font-bold">20% dto</p>
                </div>
                <div className="bg-[#222] rounded-xl p-4 text-center">
                  <p className="text-gray-400 text-sm mb-1">Premium/Competitivo</p>
                  <p className="text-[var(--brand-primary)] font-bold">30% dto</p>
                </div>
                <div className="bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-primary-strong)] rounded-xl p-4 text-center">
                  <p className="text-white/80 text-sm mb-1">Plan Gold</p>
                  <p className="text-white font-bold">50% dto</p>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Ebooks Grid */}
      <section className="px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          {/* Banner informativo - diferente para logueados vs no logueados */}
          {isFreePlan && !isLoggedIn && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-6"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-center md:text-left">
                  <h3 className="text-lg font-bold text-white mb-1">¿Primera vez aquí?</h3>
                  <p className="text-gray-400 text-sm">
                    Puedes comprar cualquier ebook sin crear cuenta. Para acceder a contenido gratuito, crea una cuenta gratis.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    onClick={() => navigate('/register')}
                    className="rounded-full font-semibold"
                    style={{ 
                      background: 'transparent',
                      border: '2px solid var(--brand-primary)',
                      color: 'var(--brand-primary)'
                    }}
                  >
                    Crear Cuenta Gratis
                  </Button>
                  <Button 
                    onClick={() => navigate('/plans')}
                    className="text-white rounded-full font-semibold"
                    style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                  >
                    Ver Planes Premium
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
          
          {/* Banner para usuarios logueados con plan free */}
          {isFreePlan && isLoggedIn && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-2xl p-6"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-center md:text-left">
                  <h3 className="text-lg font-bold text-white mb-1">¡Bienvenida de vuelta! 💪</h3>
                  <p className="text-gray-400 text-sm">
                    Mejora tu plan para obtener descuentos exclusivos en ebooks y acceso a contenido premium.
                  </p>
                </div>
                <Button 
                  onClick={() => navigate('/plans')}
                  className="text-white rounded-full font-semibold"
                  style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' }}
                >
                  Ver Planes Premium
                </Button>
              </div>
            </motion.div>
          )}

          {/* Selector de Ordenamiento */}
          <div className="flex justify-end mb-6">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 rounded-full text-sm bg-white/5 border border-white/10 text-gray-300 focus:border-pink-500 focus:outline-none"
            >
              <option value="title-asc">📝 Nombre (A-Z)</option>
              <option value="title-desc">📝 Nombre (Z-A)</option>
              <option value="date-desc">📅 Más recientes</option>
              <option value="date-asc">📅 Más antiguos</option>
              <option value="price-asc">💰 Precio (menor a mayor)</option>
              <option value="price-desc">💰 Precio (mayor a menor)</option>
              <option value="category-asc">📁 Categoría (A-Z)</option>
            </select>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sortEbooks(ebooks).map((ebook, idx) => {
              const prices = getEbookPrice(ebook.price, ebook.original_price);
              const isAccessible = canAccessEbook(ebook);
              // LÓGICA CORREGIDA:
              // - Contenido GRATUITO (is_free=true) → Bloqueado si no tiene plan (isFreePlan)
              // - Contenido de PAGO (is_free=false) → Siempre mostrar botón de compra (cualquiera puede comprarlo)
              const isLockedForFree = isFreePlan && ebook.is_free; // Solo bloquear contenido GRATIS para usuarios sin plan
              
              return (
                <motion.div
                  key={ebook.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + idx * 0.1 }}
                  className="group"
                  data-testid={`ebook-card-${idx}`}
                >
                  <div className={`bg-[#1a1a1a] rounded-2xl overflow-hidden border transition-all duration-300 h-full flex flex-col relative ${
                    isLockedForFree 
                      ? 'border-white/5 opacity-75' 
                      : 'border-white/5 hover:border-[var(--brand-primary)]/50'
                  }`}>
                    {/* Overlay de bloqueo para contenido GRATUITO cuando no tiene cuenta/plan */}
                    {isLockedForFree && (
                      <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/90 to-orange-500/90 flex flex-col items-center justify-center z-20 rounded-2xl">
                        <Lock className="h-10 w-10 text-white mb-3" />
                        <span className="text-white font-bold text-lg mb-1">Contenido Gratuito</span>
                        <span className="text-white/80 text-sm mb-4 text-center px-4">Crea una cuenta gratis para acceder</span>
                        <Button 
                          size="sm"
                          onClick={() => navigate('/register')}
                          className="bg-white text-orange-600 hover:bg-gray-100"
                        >
                          Crear Cuenta Gratis
                        </Button>
                      </div>
                    )}

                    {/* Image */}
                    <div className="relative h-52 overflow-hidden">
                      <img
                        src={ebook.thumbnail_url}
                        alt={ebook.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&h=500&fit=crop';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
                      
                      {/* Badges */}
                      <Badge className="absolute top-3 left-3 bg-[var(--brand-primary)] text-white border-0">
                        {ebook.category}
                      </Badge>
                      
                      {/* Badge de GRATIS */}
                      {ebook.is_free && (
                        <Badge className="absolute top-3 right-3 bg-green-500 text-white border-0">
                          ✓ GRATIS
                        </Badge>
                      )}
                      
                      {/* Badge de incluido en plan */}
                      {!ebook.is_free && hasEbookAccess && (
                        <Badge className="absolute top-3 right-3 bg-green-500 text-white border-0">
                          ✓ Incluido
                        </Badge>
                      )}
                    </div>
                    
                    {/* Content */}
                    <div className="p-5 flex-grow flex flex-col">
                      <h3 className="text-lg font-semibold text-white mb-2 line-clamp-2">
                        {ebook.title}
                      </h3>
                      <p className="text-gray-500 text-sm mb-4 line-clamp-2 flex-grow">
                        {ebook.description}
                      </p>
                      
                      {/* Price */}
                      <div className="mb-4">
                        {hasEbookAccess || prices.free ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            {prices.strikePrice && (
                              <span className="text-gray-600 line-through text-sm">${prices.strikePrice}</span>
                            )}
                            <span className="text-gray-500 line-through text-sm">${prices.original}</span>
                            <span className="text-[var(--brand-primary)] font-bold text-xl">GRATIS</span>
                          </div>
                        ) : prices.strikePrice ? (
                          // Con precio gancho
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-gray-600 line-through text-sm">${prices.strikePrice}</span>
                            <span className="text-white font-bold text-xl">${prices.discounted || prices.original}</span>
                            <span className="text-green-400 text-xs font-medium bg-green-500/20 px-2 py-0.5 rounded-full">
                              -{prices.strikeDiscount}
                            </span>
                          </div>
                        ) : prices.discount !== '0%' ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-gray-500 line-through text-sm">${prices.original}</span>
                            <span className="text-white font-bold text-xl">${prices.discounted}</span>
                            <span className="text-[var(--brand-primary)] text-xs font-medium">-{prices.discount}</span>
                          </div>
                        ) : (
                          <span className="text-white font-bold text-xl">${prices.original}</span>
                        )}
                      </div>
                      
                      {/* Button */}
                      <Button
                        className="w-full rounded-full font-semibold text-white"
                        style={{ 
                          background: hasEbookAccess || prices.free 
                            ? 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))' 
                            : 'linear-gradient(135deg, var(--brand-primary), var(--brand-primary-strong))'
                        }}
                        onClick={() => handlePurchaseOrDownload(ebook)}
                        data-testid={`ebook-btn-${idx}`}
                      >
                        {hasEbookAccess || prices.free ? (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                            </svg>
                            Descargar
                          </>
                        ) : (
                          <>
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                            </svg>
                            Comprar ${prices.discounted || prices.original}
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {ebooks.length === 0 && (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-[#1a1a1a] flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-600" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-white mb-2">No hay ebooks disponibles</h3>
              <p className="text-gray-500">Los ebooks estarán disponibles próximamente</p>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 bg-[#111111] border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-gray-500 text-sm">© 2025 DeisyCFit. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Ebooks;
